import { useState } from "react";
import { useLocation } from "react-router-dom";

import { useCreateAccessToken } from "../../../../api/access";
import { useAuth } from "../../../../contexts/AuthContext";
import { isDevelopment } from "../../../lib/env";
import { useCurrentPrototype } from "../useCurrentPrototype";

export const SharePanel: React.FC = () =>
  isDevelopment() ? <LocalDevSharePanel /> : <ProdSharePanel />;

const LocalDevSharePanel: React.FC = () => {
  const location = useLocation();
  const prompt = `/playground-share-proto ${location.pathname}`;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-64 rounded-xl border border-[#2a2a2a] bg-[#141414] p-4 shadow-[0_2px_8px_rgba(0,0,0,0.25)]">
      <h3 className="mb-2 text-[13px] font-semibold text-(--slate-color-action-text-inverse)">
        Local sharing
      </h3>
      <p className="mb-3 text-[11px] leading-relaxed text-[#888]">
        Local sharing is for internal use only. Paste this prompt into Claude
        Code or Cursor to spin up a public tunnel:
      </p>
      <div className="mb-2 rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] px-2.5 py-2">
        <code className="text-[10px] break-all text-[#aaa]">{prompt}</code>
      </div>
      <button
        onClick={handleCopy}
        className="w-full rounded-lg bg-[#00a6f4]/20 py-1.5 text-[12px] font-medium text-[#00bcff] transition-colors hover:bg-[#00a6f4]/30"
      >
        {copied ? "Copied!" : "Copy prompt"}
      </button>
    </div>
  );
};

const ProdSharePanel: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const currentPrototype = useCurrentPrototype();

  const defaultPath = location.pathname;
  const defaultPrototypeName = currentPrototype?.name ?? "";
  const createdBy = user?.email ?? "local";

  const [expirationHours, setExpirationHours] = useState("24");
  const [isSingleUse, setIsSingleUse] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { mutate, isPending } = useCreateAccessToken({
    onSuccess: (token) => {
      const link = `${window.location.origin}${token.path}?access=${token.token}`;
      setGeneratedLink(link);
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    },
  });

  const handleGenerate = () => {
    mutate({
      path: defaultPath,
      prototypeName: defaultPrototypeName || undefined,
      createdBy,
      expirationHours: Number(expirationHours) || 24,
      isSingleUse,
    });
  };

  const handleCopy = () => {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="w-64 rounded-xl border border-[#2a2a2a] bg-[#141414] p-4 shadow-[0_2px_8px_rgba(0,0,0,0.25)]">
      <h3 className="mb-3 text-[13px] font-semibold text-(--slate-color-action-text-inverse)">
        User research sharing
      </h3>
      {generatedLink ? (
        <div className="space-y-3">
          <p className="text-[11px] text-[#666]">
            Share this link to grant temporary access:
          </p>
          <div className="rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] px-2.5 py-2 break-all">
            <code className="text-[10px] text-[#aaa]">{generatedLink}</code>
          </div>
          <button
            onClick={handleCopy}
            className="w-full rounded-lg bg-[#00a6f4]/20 py-1.5 text-[12px] font-medium text-[#00bcff] transition-colors hover:bg-[#00a6f4]/30"
          >
            {copied ? "Copied!" : "Copy Link"}
          </button>
          <button
            onClick={() => setGeneratedLink(null)}
            className="w-full rounded-lg py-1 text-[11px] text-[#555] transition-colors hover:text-[#888]"
          >
            Generate another
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className={LABEL_CLASS}>Expiration (hours, max 168)</label>
            <input
              className={INPUT_CLASS}
              value={expirationHours}
              onChange={(event) => setExpirationHours(event.target.value)}
            />
          </div>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={isSingleUse}
              onChange={(event) => setIsSingleUse(event.target.checked)}
              className="rounded border-[#2a2a2a] bg-[#1a1a1a] accent-[#00bcff]"
            />
            <span className="text-[12px] text-[#888]">Single use</span>
          </label>
          <button
            onClick={handleGenerate}
            disabled={!defaultPath || isPending}
            className="w-full rounded-lg bg-[#00a6f4]/20 py-1.5 text-[12px] font-medium text-[#00bcff] transition-colors hover:bg-[#00a6f4]/30 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isPending ? "Generating…" : "Generate Link"}
          </button>
        </div>
      )}
    </div>
  );
};

const INPUT_CLASS =
  "w-full rounded-lg border border-[#2a2a2a] bg-[#1a1a1a] px-2.5 py-1.5 text-[12px] text-[#ccc] placeholder-[#555] outline-none focus:border-[#00a6f4]/50";
const LABEL_CLASS =
  "mb-1 block text-[10px] font-medium tracking-wide text-[#555] uppercase";
